import React, { forwardRef, useRef, useState } from 'react';
import { InputAdornment, OutlinedInput } from '@mui/material';
import type { OutlinedInputProps } from '@mui/material';
import { useForkRef } from '@mui/material/utils';
import ClearIcon from '@mui/icons-material/Clear';
import { useTranslation } from 'react-i18next';
import type { SxProps, Theme } from '@mui/material/styles';

import { IconButton } from '../Buttons/IconButton';

import type { FieldBaseProps } from './field.types';
import { FieldShell, describedBy } from './FieldShell';
import { useFieldIds } from './useFieldIds';
import { adornmentSx, controlTextSx } from './fieldStyles';
import { clearNativeInput } from './clearNativeInput';

type PassThrough = Omit<
  OutlinedInputProps,
  'size' | 'error' | 'label' | 'notched' | 'startAdornment' | 'endAdornment' | 'color' | 'margin' | 'fullWidth' | 'id' | 'required' | 'disabled' | 'className' | 'sx' | 'type'
>;

export interface TextInputProps extends FieldBaseProps, PassThrough {
  type?: 'text' | 'email' | 'tel' | 'url' | 'password' | 'search';
  onValueChange?: (value: string) => void;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  clearable?: boolean;
  onClear?: () => void;
  inputSx?: SxProps<Theme>;
}

export const TextInput = forwardRef<HTMLDivElement, TextInputProps>(function TextInput(props, ref) {
  const {
    id: idProp, label, hideLabel, helperText, error, required, hideRequiredMark, disabled, size = 'small', fullWidth = true, className, sx,
    type = 'text', value, defaultValue, onChange, onValueChange, startAdornment, endAdornment, clearable, onClear, inputRef, inputProps, inputSx, readOnly,
    describedById,
    ...rest
  } = props;
  const { t } = useTranslation();
  const ids = useFieldIds(idProp);
  const localInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const handleInputRef = useForkRef(inputRef, localInputRef);

  const isControlled = value !== undefined;
  const [uncontrolledText, setUncontrolledText] = useState(() => String(defaultValue ?? ''));
  const text = isControlled ? String(value ?? '') : uncontrolledText;

  const handleChange: OutlinedInputProps['onChange'] = (event) => {
    if (!isControlled) setUncontrolledText(event.target.value);
    onChange?.(event);
    onValueChange?.(event.target.value);
  };

  const clear = () => {
    const input = localInputRef.current;
    if (input) clearNativeInput(input);
    onClear?.();
    input?.focus();
  };

  const showClear = Boolean(clearable) && !disabled && !readOnly && text.length > 0;
  const end =
    showClear || endAdornment ? (
      <InputAdornment position="end">
        {showClear && (
          <IconButton edge="end" label={t('coreComponents.fields.clear', 'Clear')} onClick={clear}>
            <ClearIcon fontSize="small" />
          </IconButton>
        )}
        {endAdornment}
      </InputAdornment>
    ) : undefined;

  return (
    <FieldShell
      ref={ref}
      ids={ids}
      label={label}
      hideLabel={hideLabel}
      helperText={helperText}
      error={error}
      required={required}
      hideRequiredMark={hideRequiredMark}
      disabled={disabled}
      size={size}
      fullWidth={fullWidth}
      className={className}
      sx={sx}
    >
      <OutlinedInput
        id={ids.id}
        type={type}
        notched={false}
        readOnly={readOnly}
        inputRef={handleInputRef}
        inputProps={{
          ...inputProps,
          'aria-describedby': describedBy(ids, helperText, describedById, inputProps?.['aria-describedby']),
        }}
        startAdornment={
          startAdornment ? (
            <InputAdornment position="start" sx={adornmentSx}>
              {startAdornment}
            </InputAdornment>
          ) : undefined
        }
        endAdornment={end}
        onChange={handleChange}
        sx={[controlTextSx(size), ...(Array.isArray(inputSx) ? inputSx : [inputSx])]}
        {...(isControlled ? { value: text } : { defaultValue })}
        {...rest}
      />
    </FieldShell>
  );
});
